import {createContext} from 'react';

import type {Item} from './typedef';

/**
 * Shape of the context value produced by `useArrowNavigation` and consumed by
 * `useArrowNavigationItem`. Provides the registration and activation methods
 * that individual navigable items use to participate in the keyboard group.
 */
export type ArrowNavigationContextType = {
    /**
     * Registers an item in the navigation group. Called automatically by
     * `useArrowNavigationItem` on mount. The first registered item is set as
     * the active (pointed) item by default.
     */
    addItem: (item: Item) => void,
    /**
     * Deregisters an item from the navigation group. Called automatically by
     * `useArrowNavigationItem` on unmount. If the removed item is currently
     * active, focus shifts to the adjacent item.
     */
    removeItem: (item: Item) => void,
    /**
     * Imperatively marks an item as the active navigation target, updating its
     * `tabIndex` to `0` and removing that status from the previously active item.
     */
    setActiveItem: (item: Item) => void,
};
export const ArrowNavigationContext = createContext<ArrowNavigationContextType>({
    addItem: () => {},
    removeItem: () => {},
    setActiveItem: () => {},
});

ArrowNavigationContext.displayName = 'ArrowNavigationContext';
